// src/@crema/utility/AppContextProvider/SidebarContextProvider.js
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import defaultConfig from './defaultConfig';

const SidebarContext = createContext({
  // safe defaults to avoid undefined access before provider mounts
  menuStyle: defaultConfig.sidebar.menuStyle,
  isSidebarBgImage: defaultConfig.sidebar.isSidebarBgImage,
  sidebarBgImage: defaultConfig.sidebar.sidebarBgImage,
  borderColor: defaultConfig.sidebar.borderColor,
  ...defaultConfig.sidebar.colorSet, // textColor, bgColor, etc (whatever your theme defines)
});

const SidebarActionsContext = createContext({
  updateMenuStyle: () => {},
  updateSidebarColorSet: () => {},
  setSidebarBgImageEnabled: () => {},
  updateSidebarBgImage: () => {},
  // alias for backwards compatibility (optional)
  setSidebarBgImage: () => {},
});

export const useSidebarContext = () => useContext(SidebarContext);
export const useSidebarActionsContext = () => useContext(SidebarActionsContext);

const SidebarContextProvider = ({children}) => {
  const [menuStyle, updateMenuStyle] = useState(
    defaultConfig.sidebar.menuStyle,
  );
  const [sidebarColorSet, updateSidebarColorSet] = useState(
    defaultConfig.sidebar.colorSet,
  );
  const [isSidebarBgImage, setIsSidebarBgImage] = useState(
    defaultConfig.sidebar.isSidebarBgImage,
  );
  const [sidebarBgImage, setSidebarBgImageState] = useState(
    defaultConfig.sidebar.sidebarBgImage,
  );

  // clearer names: one toggles the *flag*, the other sets the *image*
  const setSidebarBgImageEnabled = useCallback((enabled) => {
    setIsSidebarBgImage(Boolean(enabled));
  }, []);

  const updateSidebarBgImage = useCallback((img) => {
    setSidebarBgImageState(img);
  }, []);

  // avoid re-renders in consumers by memoizing provided values
  const sidebarValue = useMemo(
    () => ({
      ...sidebarColorSet,
      menuStyle,
      isSidebarBgImage,
      sidebarBgImage,
      borderColor: defaultConfig.sidebar.borderColor,
    }),
    [sidebarColorSet, menuStyle, isSidebarBgImage, sidebarBgImage],
  );

  const actionsValue = useMemo(
    () => ({
      updateMenuStyle,
      updateSidebarColorSet,
      setSidebarBgImageEnabled,
      updateSidebarBgImage,
      // alias for old callers; you can remove once codebase is updated
      setSidebarBgImage: setSidebarBgImageEnabled,
    }),
    [
      updateMenuStyle,
      updateSidebarColorSet,
      setSidebarBgImageEnabled,
      updateSidebarBgImage,
    ],
  );

  return (
    <SidebarContext.Provider value={sidebarValue}>
      <SidebarActionsContext.Provider value={actionsValue}>
        {children}
      </SidebarActionsContext.Provider>
    </SidebarContext.Provider>
  );
};

SidebarContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SidebarContextProvider;
